<template>
  <AllocationCardLayout
    icon="calendar-alt"
    :color="PALETTE.semanticOverrides.portfolioType.Forward"
    :title="$t('production_strategy.forward_sales')"
    :amount="allocation.amount"
    :unit="$t('common.mwh')"
    :subtitle="$t('production_strategy.allocated')"
    :ariaLabel="$t('production_strategy.forward_allocation_card')"
    :expandable="true"
    :expandButtonText="$t('production_strategy.forward_sales_overview.title')"
    :hasExpandableContent="true"
    :framedContent="false"
    @toggled="onToggled"
  >
    <template #badge>
      <span
        class="badge text-white px-3 py-2"
        :style="{
          backgroundColor: PALETTE.semanticOverrides.portfolioType.Forward,
        }"
      >
        {{ allocation.percentagePerQuarter ?? 0 }}%
        {{ $t("production_strategy.of_projected_production_per_quarter") }}
      </span>
    </template>
    <template #action>
      <button
        type="button"
        class="btn btn-outline-primary btn-sm"
        data-testid="add-forward-pool"
        @click="poolEditor.open(null)"
      >
        <FontAwesomeIcon :icon="['fas', 'plus']" class="me-1" />
        {{ $t("common.add") }}
      </button>
    </template>
    <template #header-extra>
      <!-- Status Info with Connected Bar -->
      <div v-if="hasProgress" class="status-info mt-3">
        <!-- Status Labels -->
        <div class="d-flex justify-content-between mb-2">
          <div class="d-flex align-items-center">
            <FontAwesomeIcon icon="check-circle" class="text-success me-2" />
            <span class="small"
              >{{ $t("production_strategy.already_committed") }}
              <strong
                >{{ formatMwh(allocation.committedAmount || 0) }}
                {{ $t("common.mwh") }}</strong
              ></span
            >
          </div>
          <div class="d-flex align-items-center">
            <FontAwesomeIcon icon="clock" class="text-primary me-2" />
            <span class="small"
              >{{ $t("production_strategy.for_upcoming_sales") }}
              <strong
                >{{ formatMwh(allocation.upcomingAmount || 0) }}
                {{ $t("common.mwh") }}</strong
              ></span
            >
          </div>
        </div>

        <!-- Connected Progress Bar -->
        <div class="progress" style="height: 6px">
          <div
            class="progress-bar bg-success"
            role="progressbar"
            :style="{ width: committedPercentage + '%' }"
            :aria-valuenow="committedPercentage"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
          <div
            class="progress-bar bg-primary"
            role="progressbar"
            :style="{ width: upcomingPercentage + '%' }"
            :aria-valuenow="upcomingPercentage"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
      </div>
    </template>
    <!-- Unframed: the timeline brings its own box, and another frame around it only nests cards. -->
    <template #content>
      <div class="pt-2">
        <PoolsTimelinePanelComponent
          v-if="everExpanded"
          ref="panel"
          :key="panelSeed.join(',')"
          :companyIds="panelSeed"
          :country="currentCompany?.country ?? null"
        />
      </div>
    </template>
  </AllocationCardLayout>

  <PoolEditorModalHost @written="onPoolWritten" />
</template>

<script setup lang="ts">
import { PALETTE } from "@/composables/chartPalette";
import { formatMwh } from "@/composables/formatUtils";
import PoolEditorModalHost from "@/forwards2/pools/PoolEditorModalHost.vue";
import PoolsTimelinePanelComponent from "@/forwards2/pools/PoolsTimelinePanelComponent.vue";
import { usePoolEditorLauncher } from "@/forwards2/pools/usePoolEditorLauncher";
import type { Company } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, ref, useTemplateRef } from "vue";

import AllocationCardLayout from "../AllocationCardLayout.vue";
import { useCompanyMode } from "../useCompanyMode";
import type { AllocationData } from "../useProductionAllocationData";

/**
 * The dashboard's forward sales card: the simulation's forward allocation, opening onto the pools
 * timeline (which brings the forward sales materials and the guide to setting up pools); its add button opens the pool editor, as the PPA card's
 * opens a new agreement. The editor also opens from a pool's name in the timeline.
 *
 * The timeline loads when the card is first expanded. A single-company view seeds its company
 * filter with that company; the consolidated view seeds none, so every pool in the account's scope
 * shows. The allocation figures are simulated from the pools on every request, so the card emits
 * `updated` after an editor session that wrote, and the dashboard refetches them.
 */
const props = withDefaults(
  defineProps<{
    allocation: AllocationData["forward"];
    /** The dashboard's companies; exactly one is a single-company view. */
    companies?: Company[];
  }>(),
  { companies: () => [] }
);

const emit = defineEmits<{
  updated: [];
}>();

const poolEditor = usePoolEditorLauncher();
const everExpanded = ref(false);
const panel =
  useTemplateRef<InstanceType<typeof PoolsTimelinePanelComponent>>("panel");

const { currentCompany } = useCompanyMode(computed(() => props.companies));

const panelSeed = computed(() =>
  currentCompany.value?.id === undefined ? [] : [currentCompany.value.id]
);

// Calculate percentages for progress bars
const hasProgress = computed(() => {
  return (
    (props.allocation.committedAmount || 0) +
      (props.allocation.upcomingAmount || 0) >
    0
  );
});

const committedPercentage = computed(() => {
  const total =
    (props.allocation.committedAmount || 0) +
    (props.allocation.upcomingAmount || 0);
  if (total === 0) return 0;
  return Math.round(((props.allocation.committedAmount || 0) / total) * 100);
});

const upcomingPercentage = computed(() => {
  const total =
    (props.allocation.committedAmount || 0) +
    (props.allocation.upcomingAmount || 0);
  if (total === 0) return 0;
  return Math.round(((props.allocation.upcomingAmount || 0) / total) * 100);
});

// The allocation figures are simulated from the pools, and the panel lists them.
const onPoolWritten = () => {
  emit("updated");
  panel.value?.reload();
};

const onToggled = (expanded: boolean) => {
  if (expanded) everExpanded.value = true;
};
</script>

<style scoped lang="scss">
.status-info {
  .progress {
    background-color: var(--bs-gray-200);
  }
}
</style>
